/**
 * Drawing background.
 *
 * Dark, matching CAD model-space convention and the DXF prototype: DXF colour
 * tables assume a dark canvas, so light entity colours (the common case) and the
 * text layer both read correctly against it. On white, a large share of a typical
 * drawing renders invisible.
 *
 * Owned by the shell rather than the renderer so that the container can paint it
 * immediately — the renderer arrives in a lazily-loaded chunk, and without this the
 * viewer would flash the page background while that chunk resolves.
 */
export const DXF_CANVAS_BACKGROUND = '#1e1e1e';

/**
 * Colour a picked item takes.
 *
 * The product accent, so selection reads the same here as everywhere else in the
 * app.
 */
export const SELECTION_ACCENT_COLOR = '#2f80ed';

/**
 * Colour geometry takes while the pointer is over it.
 *
 * Deliberately NOT the selection accent: hovering is a promise, not a state, and
 * using the accent for both makes a drawing look half-selected as the cursor
 * crosses it. A neutral halo reads as "this is what you would get".
 */
export const HOVER_ACCENT_COLOR = '#d8d8d8';

/**
 * Accent stroke widths, in CSS pixels.
 *
 * Wider than any lineweight the drawing itself uses, so an accent reads as a halo
 * around the stroke rather than a recolouring of it — which matters because the
 * underlying geometry keeps its own colour underneath.
 */
export const SELECTION_ACCENT_WIDTH_PX = 4;
export const HOVER_ACCENT_WIDTH_PX = 3;

/**
 * Colour an item takes while a marquee is poised to collect it.
 *
 * A lighter cast of the selection accent, and narrower: the preview has to read
 * as "this is what you are about to get" without being mistaken for something
 * already selected, since the two are on screen together throughout the drag.
 */
export const PREVIEW_ACCENT_COLOR = '#7fb3f2';

/** Preview colour while the box is subtracting, matching the marquee's restyle. */
export const PREVIEW_SUBTRACT_COLOR = '#f0b48a';

export const PREVIEW_ACCENT_WIDTH_PX = 3;

/**
 * Pick radius in CSS pixels.
 *
 * A screen distance rather than a world one, converted through the current zoom at
 * pick time, so hitting a line feels identical at every zoom level.
 */
export const PICK_TOLERANCE_PX = 6;

/**
 * How far outside its drawn border a marquee still collects, in CSS pixels.
 *
 * A stroke is drawn one to two pixels wide centred on its mathematical line, so
 * the box *looks* like it has made contact slightly before it geometrically has.
 * Without this, whether an edge is caught comes down to sub-pixel placement the
 * user cannot see or control — which reads as flakiness even though the maths is
 * exact. Deliberately well under the pick radius: a marquee is a deliberate area
 * gesture, and too much forgiveness would grab things it visibly misses.
 */
export const MARQUEE_TOLERANCE_PX = 3;

/**
 * Snap radius in CSS pixels, for the measure tool.
 *
 * Wider than the pick radius: a snap is a deliberate assist and should engage
 * before the user has to be precise, whereas a pick should land on exactly what
 * they aimed at.
 */
export const SNAP_TOLERANCE_PX = 12;

/** Marquee preview. Solid encloses, dashed crosses; subtracting restyles. */
export const MARQUEE_STROKE_COLOR = '#2f80ed';
export const MARQUEE_FILL_COLOR = 'rgba(47, 128, 237, 0.12)';
export const MARQUEE_SUBTRACT_STROKE_COLOR = '#e2854b';
export const MARQUEE_SUBTRACT_FILL_COLOR = 'rgba(226, 133, 75, 0.12)';

/** Measure tool. */
export const MEASURE_COLOR = '#ffd166';
export const SNAP_RING_RADIUS_PX = 6;

/** Axis-aligned dimensions, colour-coded per axis. */
export const DIMENSION_X_COLOR = '#e56b6f';
export const DIMENSION_Y_COLOR = '#6bcf8f';
/** Optimal bounding box outline. */
export const OPTIMAL_BOX_COLOR = '#9b8cf0';

/** Colour the drawing takes when layer colouring is switched off. */
export const MONOCHROME_COLOR = '#d8d8d8';

/**
 * DOM text-layer colour (CSS overlay, not WebGL).
 *
 * Near-opaque white so annotations stay readable on the dark canvas without
 * washing out thin strokes underneath.
 */
export const TEXT_LAYER_COLOR = 'rgba(255, 255, 255, 0.92)';

/**
 * Rotate-control chip colours — match partviewer's `DxfRotateButton` so the
 * legacy DXF path and this viewer share the same chrome.
 */
export const ROTATE_CHIP_BG = 'rgba(41, 41, 41, 0.75)';
export const ROTATE_CHIP_BG_HOVER = 'rgba(41, 41, 41, 0.85)';
